/*
  Sticky add-to-cart bar. Two jobs:

  1. Visibility — reveal once the real add-to-cart button has scrolled off the
     top of the viewport, and hide again once the footer is in view so the bar
     never covers it.
  2. Sync — Dawn replaces the buy-buttons markup wholesale on a variant change,
     so the bar mirrors the real button's label and disabled state rather than
     tracking variant data itself. Whatever the real button says is true.
*/
class ProductStickyCart extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sticky-submit]');
    this.variantText = this.querySelector('[data-sticky-variant]');
    this.form = document.getElementById(this.dataset.formId);
    this.productInfo = this.closest('product-info') || document;

    this.watchTrigger();
    this.watchFooter();
    this.watchRealButton();
    this.querySelector('.product-sticky-cart__change')?.addEventListener('click', this.onChange.bind(this));
  }

  disconnectedCallback() {
    this.triggerObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.formObserver?.disconnect();
  }

  get realButton() {
    return this.productInfo.querySelector('.product-form__submit');
  }

  watchTrigger() {
    const target = this.realButton;
    if (!target) return;
    this.triggerObserver = new IntersectionObserver(
      ([entry]) => {
        /* Only when it has left past the TOP — otherwise the bar would also
           show before the button has been scrolled to on first paint. */
        this.scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.render();
      },
      { threshold: 0 }
    );
    this.triggerObserver.observe(target);
  }

  /* Hide only at the very end of the document, not the moment the footer
     appears. Our product pages are short enough that "footer is on screen"
     covers most of the scrollable range, which left the bar never showing at
     all — the reference only drops it once you have actually reached the
     bottom. */
  watchFooter() {
    const footer = document.querySelector('.footer, footer');
    if (!footer) return;
    this.footerObserver = new IntersectionObserver(
      ([entry]) => {
        this.atPageEnd = entry.boundingClientRect.bottom <= window.innerHeight + 40;
        this.render();
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    this.footerObserver.observe(footer);
  }

  /* Dawn swaps the whole buy-buttons block on variant change, so watch the
     container rather than holding a reference to a button that gets replaced. */
  watchRealButton() {
    const container = this.productInfo.querySelector('.product-form__buttons') || this.form;
    if (!container) return;
    this.formObserver = new MutationObserver(() => this.sync());
    this.formObserver.observe(container, { childList: true, subtree: true, attributes: true });
    this.sync();
  }

  sync() {
    const real = this.realButton;
    if (real && this.button) {
      const label = real.querySelector('span:not(.visually-hidden)') || real;
      const text = label.textContent.trim();
      if (text) this.button.textContent = text;
      this.button.disabled = real.hasAttribute('disabled');
    }
    if (this.variantText) {
      const selected = this.selectedOptions();
      if (selected) this.variantText.textContent = selected;
    }
    /* A replaced button is a new node — re-point the trigger observer at it. */
    if (real && this.triggerObserver) {
      this.triggerObserver.disconnect();
      this.triggerObserver.observe(real);
    }
  }

  selectedOptions() {
    const picker = this.productInfo.querySelector('variant-selects');
    if (!picker) return null;
    const values = [];
    picker.querySelectorAll('select').forEach((s) => values.push(s.value));
    picker.querySelectorAll('input[type="radio"]:checked').forEach((i) => values.push(i.value));
    return values.length ? values.join(' / ') : null;
  }

  onChange(event) {
    const picker = this.productInfo.querySelector('variant-selects');
    if (!picker) return;
    event.preventDefault();
    picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const first = picker.querySelector('select, input:not([type="hidden"])');
    if (first) setTimeout(() => first.focus({ preventScroll: true }), 400);
  }

  render() {
    this.classList.toggle('is-visible', Boolean(this.scrolledPast) && !this.atPageEnd);
  }
}

customElements.define('product-sticky-cart', ProductStickyCart);
